import { Provider } from '@/types/providers'

/**
 * Anthropic exposes 1M-context variants as a `:1m` suffix on an otherwise ordinary model id
 * (e.g. `claude-sonnet-4-6:1m`). The suffix selects a context window / beta, it is never part of
 * the family or version, so every model-capability check normalizes it away first.
 *
 * Mirrors `missionsquad-core/src/index.ts` (`shouldDefaultToPTC`: `model.replace(/:1m$/, '')`).
 */
export function normalizeModelId(model: string): string {
  return model.replace(/:1m$/, '')
}

/**
 * Client-side replica of the server-side Programmatic Tool Calling default rule.
 *
 * KEEP IN SYNC — the authoritative implementation is `shouldDefaultToPTC` in
 * `missionsquad-core/src/index.ts` (private method, applied in `addModel` when the request
 * omits `programmaticToolCalling`, and in `migrateModelConfigsForPTC`). The dashboard needs its
 * own copy because `ModelCard.vue` always sends an explicit boolean, so core's default never
 * fires for UI-created configs — the checkbox is pre-checked with this rule instead.
 *
 * When a NEW Anthropic model family ships (or the version floor moves), the change MUST be made
 * in BOTH places: `missionsquad-core/src/index.ts` `shouldDefaultToPTC` and this function.
 * Divergence silently changes whether dashboard-created models opt into PTC.
 *
 * Rule:
 * - anthropic only;
 * - families `sonnet` / `opus` qualify at version >= 4.6; `fable` / `mythos` always qualify
 *   (they have no pre-4.6 history); `haiku` never qualifies;
 * - a trailing 8-digit segment is a release date (`claude-sonnet-4-20250514`), not a minor
 *   version, so it must not be read as one.
 *
 * @param providerKey - The configured provider key (only `anthropic` can qualify).
 * @param model - The provider-native model id, with or without a `:1m` suffix.
 * @returns True when the PTC checkbox should default to checked for this provider + model.
 */
export function defaultsToPTC(providerKey: string, model: string): boolean {
  if (providerKey !== Provider.Anthropic) {
    return false
  }

  const normalizedModel = normalizeModelId(model)
  const familyMatch = normalizedModel.match(/^claude-(sonnet|opus|haiku|fable|mythos)-(.+)$/)
  if (!familyMatch) {
    return false
  }

  const family = familyMatch[1]
  if (family === 'haiku') {
    return false
  }
  if (family === 'fable' || family === 'mythos') {
    // No pre-4.6 history, so any version qualifies — including dated ids such as
    // `claude-fable-5-20260101`.
    return true
  }

  const versionParts = familyMatch[2].split('-')
  const major = Number(versionParts[0])
  if (!Number.isInteger(major)) {
    return false
  }

  let minor = 0
  if (versionParts.length > 1 && !/^\d{8}$/.test(versionParts[1])) {
    minor = Number(versionParts[1])
    if (!Number.isInteger(minor)) {
      return false
    }
  }

  return major > 4 || (major === 4 && minor >= 6)
}

/**
 * Anthropic models that reject non-default sampling parameters (`temperature` / `top_p`) with a
 * 400. Requests do not fail in practice because rosetta strips the params before they reach
 * Anthropic — which means any value the user sets in the dashboard is silently ignored.
 *
 * KEEP IN SYNC with `modelsWithoutSamplingParams` in
 * `rosetta-ai-sdk/src/core/mapping/anthropic.mapper.ts` (the set consumed by
 * `shouldStripSamplingParams`).
 *
 * This is a client-side mirror ONLY because the model registry metadata does not expose a
 * sampling-params flag: `RosettaModel.properties` (see `src/types/msq.ts`) carries
 * vision / extended_thinking / cost / latency, and nothing about sampling. Switch this function
 * over to that metadata as soon as the registry exposes such a flag, and delete the list.
 */
const MODELS_WITHOUT_SAMPLING_PARAMS: ReadonlySet<string> = new Set([
  'claude-opus-4-7',
  'claude-opus-4-8',
  'claude-opus-5',
  'claude-sonnet-5',
  'claude-fable-5'
])

/**
 * Whether `temperature` / `top_p` are meaningful for a provider + model pair.
 *
 * @param providerKey - The configured provider key (only `anthropic` has exclusions).
 * @param model - The provider-native model id, with or without a `:1m` suffix.
 * @returns False when the model ignores/rejects sampling params, true otherwise.
 */
export function supportsSamplingParams(providerKey: string, model: string): boolean {
  if (providerKey !== Provider.Anthropic) {
    return true
  }
  return !MODELS_WITHOUT_SAMPLING_PARAMS.has(normalizeModelId(model))
}

/**
 * True when `model` is absent from the provider's discovered model list — i.e. the provider no
 * longer offers it and generation with it will fail. Stored configs are never rewritten; the UI
 * only warns so the user re-selects.
 *
 * False-positive guard: an empty or missing list means "not fetched yet" (`setModelsList` in
 * `src/store/providers.ts` also commits `[]` on a failed fetch), so it must never be read as
 * "every model was retired" — this returns false in that case.
 *
 * Discovered ids carry the `:1m` suffix where it applies, so an exact hit is the normal case; the
 * normalized comparison keeps a suffixed config from being flagged when only the base id is
 * listed (a false negative is safe, a false positive is not).
 *
 * @param discovered - The provider's discovered model list from the providers store.
 * @param model - The configured provider-native model id.
 * @returns True only when the list is non-empty and the id is absent from it.
 */
export function isRetiredModelId(
  discovered: ReadonlyArray<{ id: string }> | null | undefined,
  model: string | null | undefined
): boolean {
  if (!model) return false
  if (!Array.isArray(discovered) || discovered.length === 0) return false
  const normalized = normalizeModelId(model)
  return !discovered.some(entry => entry.id === model || normalizeModelId(entry.id) === normalized)
}
